import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { WidgetDataResult, WidgetChartConfig, WidgetClickPoint } from '../types';
import { dashboardPalette, dashboardSentimentPaletteMap, otherCategoryColor } from '../../../utils/colors';
import { mergeEChartsOption, formatNumber, escapeHtml } from './echartsUtils';

interface ChartPieProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
  onPointClick?: (point: WidgetClickPoint) => void;
}

export const ChartPie: React.FC<ChartPieProps> = ({ data, config, onPointClick }) => {
  const mapping = config.columnMapping;

  const nameCol = mapping?.name || data.columns[0];
  const valueCol = mapping?.value || data.columns[1] || data.columns[0];

  const pieData = data.rows.map((row) => ({
    name: String(row[nameCol] ?? ''),
    value: Number(row[valueCol]) || 0,
  }));

  const colors = config.colors?.length
    ? config.colors
    : pieData.map((item, i) => {
        const key = item.name.toUpperCase();
        if (key === 'OTHER') return otherCategoryColor;
        if (config.useSentimentColors) {
          return dashboardSentimentPaletteMap[key as keyof typeof dashboardSentimentPaletteMap] ?? dashboardPalette[i % dashboardPalette.length];
        }
        return dashboardPalette[i % dashboardPalette.length];
      });

  const legendPosition = config.legend?.position || 'bottom';
  const legendConfig = useMemo(() => {
    const cfg: Record<string, unknown> = {
      type: 'scroll',
      show: legendPosition !== 'none',
      icon: 'circle',
      textStyle: { fontSize: 12 },
    };
    if (legendPosition === 'top') cfg.top = 0;
    else if (legendPosition === 'bottom') { cfg.bottom = 10; cfg.padding = [5, 10]; }
    else if (legendPosition === 'left') { cfg.left = 0; cfg.orient = 'vertical'; }
    else if (legendPosition === 'right') { cfg.right = 0; cfg.orient = 'vertical'; }
    return cfg;
  }, [legendPosition]);

  const hole = config.pieHole ?? 0.5;
  const innerRadius = hole > 0 ? `${Math.round(hole * 70)}%` : '0%';

  const option = useMemo(() => {
    const base = {
      color: colors,
      tooltip: {
        trigger: 'item' as const,
        formatter: (params: any) => {
          const val = typeof params.value === 'number' ? formatNumber(params.value) : escapeHtml(params.value);
          return `${params.marker}${escapeHtml(params.name)}: <b>${val}</b> (${params.percent}%)`;
        },
        appendToBody: true,
      },
      legend: legendConfig,
      series: [
        {
          type: 'pie' as const,
          radius: [innerRadius, '60%'],
          center: legendPosition === 'bottom' ? ['50%', '44%'] : legendPosition === 'top' ? ['50%', '56%'] : ['50%', '50%'],
          data: pieData,
          label: { formatter: '{d}%', fontSize: 12 },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.3)',
            },
          },
        },
      ],
    };
    return mergeEChartsOption(base, config.echartsOption);
  }, [colors, legendConfig, legendPosition, innerRadius, pieData, config.echartsOption]);

  const onEvents = useMemo(() => {
    if (!onPointClick) return undefined;
    return {
      click: (params: any) => {
        const sliceName = String(params.name ?? '');
        // Find the original row for this slice
        const row = data.rows.find((r) => String(r[nameCol]) === sliceName);
        onPointClick({
          label: sliceName,
          key: sliceName,
          value: params.value ?? 0,
          extra: row ? { ...row } as Record<string, string | number | boolean | null> : undefined,
        });
      },
    };
  }, [onPointClick, data.rows, nameCol]);

  return (
    <ReactECharts
      option={option}
      style={{ width: '100%', height: '100%' }}
      opts={{ renderer: 'svg' }}
      onEvents={onEvents}
    />
  );
};
